import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Check, X, Sparkles, Minus, Plus, Film } from 'lucide-react';
import {
  AI_VIDEO_PRICING,
  AI_VIDEO_CURRENCY_SYMBOL,
  AI_VIDEO_FEATURES,
  AI_VIDEO_MIN_QTY,
  AI_VIDEO_MAX_QTY,
} from '@/lib/aiVideoPricing';
import { detectDefaultCurrency, setStoredCurrency } from '@/lib/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

function formatAmount(n) {
  return Number(n).toLocaleString('en-US');
}

export default function AIVideoPaywallModal({ open, onClose, mode = 'unlock', onSeeSamples }) {
  // mode: 'unlock' (first-time) | 'topup' (already unlocked, buying more)
  const [currency, setCurrencyState] = useState(() => detectDefaultCurrency());
  const setCurrency = (c) => { setCurrencyState(c); setStoredCurrency(c); };
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const sym = AI_VIDEO_CURRENCY_SYMBOL[currency] || '£';
  const unit = AI_VIDEO_PRICING[currency] || AI_VIDEO_PRICING.gbp;
  const total = unit * qty;

  const dec = () => setQty((q) => Math.max(AI_VIDEO_MIN_QTY, q - 1));
  const inc = () => setQty((q) => Math.min(AI_VIDEO_MAX_QTY, q + 1));
  const onQtyInput = (v) => {
    const n = parseInt(v, 10);
    if (Number.isNaN(n)) { setQty(AI_VIDEO_MIN_QTY); return; }
    setQty(Math.min(AI_VIDEO_MAX_QTY, Math.max(AI_VIDEO_MIN_QTY, n)));
  };

  const handleCheckout = async () => {
    setLoading(true);
    try { localStorage.setItem('ai_video_paying_at', new Date().toISOString()); } catch (_e) { /* ignore */ }
    try {
      const res = await axios.post(
        `${API}/stripe/ai-video-checkout`,
        { currency, quantity: qty },
        { headers: authHeaders() }
      );
      const url = res?.data?.url;
      if (url) {
        window.location.href = url;
        return;
      }
      // No URL returned - open the static link as a last resort
      window.location.href = `https://stripe.com`;
    } catch (_e) {
      // On error, fall back to opening a mailto so the user can reach support
      const subject = encodeURIComponent('Activate AI Video - purchase failed');
      const body = encodeURIComponent(`Hi,\n\nI tried to purchase ${qty} AI video credit(s) (${sym}${formatAmount(total)} total) but checkout failed. Please assist.\n\nThanks!`);
      window.location.href = `mailto:lawagency360@gmail.com?subject=${subject}&body=${body}`;
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center px-4 py-8 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ai-video-paywall-title"
      data-testid="ai-video-paywall-modal"
    >
      <button
        type="button"
        aria-label="Close paywall"
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        data-testid="ai-video-paywall-backdrop"
      />
      <div className="relative w-full max-w-2xl rounded-3xl bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 border border-violet-500/30 p-7 shadow-2xl shadow-violet-900/40 my-auto">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors"
          aria-label="Close"
          data-testid="ai-video-paywall-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-xl bg-violet-500/15 border border-violet-500/30 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-violet-300" />
          </div>
          <div>
            <h2 id="ai-video-paywall-title" className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>
              {mode === 'topup' ? 'Buy More Videos' : 'Unlock AI Video Production'}
            </h2>
            <p className="text-sm text-zinc-400 mt-0.5">
              {mode === 'topup'
                ? 'Top up your video credits - same premium quality, billed once.'
                : 'High-quality AI video production. Pay-per-video, no subscription.'}
            </p>
          </div>
        </div>

        <div className="flex justify-end mt-5 mb-4">
          <div className="inline-flex p-1 bg-white/5 border border-white/10 rounded-full" data-testid="ai-video-paywall-currency">
            {[
              { code: 'gbp', label: 'GBP £' },
              { code: 'eur', label: 'EUR €' },
              { code: 'usd', label: 'USD $' },
            ].map((c) => (
              <button
                key={c.code}
                onClick={() => setCurrency(c.code)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition ${
                  currency === c.code ? 'bg-white/15 text-white' : 'text-slate-400 hover:text-white'
                }`}
                data-testid={`ai-video-paywall-currency-${c.code}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div
          className="relative rounded-2xl p-6 bg-gradient-to-b from-violet-900/30 to-indigo-900/20 border border-violet-500/40 shadow-xl shadow-violet-900/30"
          data-testid="ai-video-paywall-product"
        >
          <div className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-gradient-to-r from-violet-500 to-indigo-500 text-[10px] font-bold uppercase tracking-wider text-white shadow-lg">
            High Quality
          </div>

          <h3 className="text-xl font-bold text-white mt-1" style={{ fontFamily: 'Outfit' }}>
            AI Video Production
          </h3>
          <p className="text-sm text-violet-200 mt-1">Premium content production - billed per video.</p>

          <div className="flex items-baseline gap-1 mt-4">
            <span className="text-3xl font-bold text-white" data-testid="ai-video-paywall-unit-price">
              {sym}{formatAmount(unit)}
            </span>
            <span className="text-sm text-zinc-400">/ video</span>
          </div>

          <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-2 mt-5" data-testid="ai-video-paywall-features">
            {AI_VIDEO_FEATURES.map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-zinc-200">
                <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>

          <div className="mt-6 pt-5 border-t border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Quantity</div>
              <div className="mt-2 inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full p-1" data-testid="ai-video-paywall-quantity">
                <button
                  onClick={dec}
                  disabled={qty <= AI_VIDEO_MIN_QTY}
                  className="w-9 h-9 rounded-full hover:bg-white/10 text-white flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed transition"
                  aria-label="Decrease quantity"
                  data-testid="ai-video-paywall-qty-dec"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <input
                  type="number"
                  value={qty}
                  min={AI_VIDEO_MIN_QTY}
                  max={AI_VIDEO_MAX_QTY}
                  onChange={(e) => onQtyInput(e.target.value)}
                  className="w-14 bg-transparent text-center text-white font-bold text-lg focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  data-testid="ai-video-paywall-qty-input"
                />
                <button
                  onClick={inc}
                  disabled={qty >= AI_VIDEO_MAX_QTY}
                  className="w-9 h-9 rounded-full hover:bg-white/10 text-white flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed transition"
                  aria-label="Increase quantity"
                  data-testid="ai-video-paywall-qty-inc"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="text-right">
              <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Total</div>
              <div className="text-2xl font-bold text-white mt-1" data-testid="ai-video-paywall-total">
                {sym}{formatAmount(total)}
              </div>
              <div className="text-[11px] text-zinc-500">{qty} video{qty === 1 ? '' : 's'} · one-time</div>
            </div>
          </div>

          <button
            onClick={handleCheckout}
            disabled={loading}
            className="w-full mt-5 py-3 rounded-xl font-semibold text-sm bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:shadow-lg hover:shadow-violet-600/40 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
            data-testid="ai-video-paywall-checkout-btn"
          >
            {loading
              ? 'Redirecting…'
              : mode === 'topup'
                ? `Buy ${qty} More Video${qty === 1 ? '' : 's'} · ${sym}${formatAmount(total)}`
                : `Continue to Checkout · ${sym}${formatAmount(total)}`}
          </button>

          {onSeeSamples && (
            <button
              type="button"
              onClick={onSeeSamples}
              className="w-full mt-3 py-3 rounded-xl font-semibold text-sm bg-white/5 border border-white/15 text-white hover:bg-white/10 hover:border-violet-500/40 transition-all flex items-center justify-center gap-2"
              data-testid="ai-video-paywall-see-samples-btn"
            >
              <Film className="w-4 h-4 text-violet-300" />
              See samples of our work
            </button>
          )}
        </div>

        <p className="text-xs text-zinc-500 mt-5 text-center">
          Your video credits activate the moment Stripe confirms payment.
        </p>
      </div>
    </div>,
    document.body
  );
}
